import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, IconButton, Typography } from '@mui/material';
import { Add, Sort } from '@mui/icons-material';
import { Form2 } from '../../../toolympus/components/primitives/Forms2';
import { PageTitleMenu } from '../../../toolympus/components/primitives/PageTitleMenu';
import {
  LoadingIndicator,
  MoreActionsMenuButton,
  OccupyFreeSpace,
  PseudoLink2,
  SearchField,
} from '../../../toolympus/components/primitives';
import {
  ReorderItemsDialog,
  useReorderItemsBySortorder,
} from '../../../toolympus/components/primitives/ReorderItems';
import { TableForFields } from '../../../toolympus/components/schemed';
import { CopTreeMenu } from './Common';
import {
  CopConference,
  updateCopConference,
  useCopConferences,
} from './useCopConferences';
import { CopConferenceEditDialog } from './CopConferenceEditDialog';

const CopConferenceReordering = (props: { item: CopConference }) => {
  return <Typography>{props.item.title}</Typography>;
};

interface Props {}

export const CopConferencesPage = (_props: Props) => {
  const data = useCopConferences();

  const reorderData = useReorderItemsBySortorder<CopConference>({
    updateItemOrder: (item, order) => {
      if (item.sort_order !== order) {
        return updateCopConference(item._id, { sort_order: order });
      }
      return Promise.resolve(item);
    },
    onSave: () => data.reload(),
  });

  return (
    <>
      <Form2
        headerItems={
          <>
            <PageTitleMenu
              current="conferences"
              items={CopTreeMenu}
              title="COP Conferences"
            />
            <IconButton
              size="small"
              color="primary"
              onClick={() => data.newConference.startEditing()}
            >
              <Add />
            </IconButton>
            <Button
              size="small"
              color="primary"
              startIcon={<Sort />}
              onClick={() => reorderData.startEditing(data.data)}
            >
              <FormattedMessage id="common.reorder" defaultMessage="Reorder" />
            </Button>


            <OccupyFreeSpace />


            {data.isLoading && <LoadingIndicator size="small" />}
            <SearchField
              filter={data.filter.filter}
              setFilter={data.filter.setFilter}
              small
              autoFocus
              noButton
              doSearch={() => {
                if(data.data.length === 1) {
                  data.editConference.startEditing({ ...data.data[0] })
                }
              }}
            />
          </>
        }
        background="none-padded"
      >
        <TableForFields
          data={data.data}
          schema={data.schema}
          fields={[
            ['_id', { width: 60 }],
            ['title'],
            ['color', { width: 100 }],
            ['is_hidden', { width: 80 }],
          ]}
          fieldElement={f => {
            switch (f) {
              case 'title':
                return (row, _schema, orig) => (
                  <PseudoLink2
                    border
                    onClick={() => data.editConference.startEditing(row)}
                  >
                    {orig}
                  </PseudoLink2>
                );
            }
          }}
          rowButtons={row => (
            <MoreActionsMenuButton
              actions={[
                [
                  'remove',
                  <FormattedMessage id="common.delete" defaultMessage="Delete" />,
                  () => data.removeConference.run(row),
                ],
              ]}
            />
          )}
        />
      </Form2>

      <CopConferenceEditDialog
        {...data.newConference}
        isOpen={data.newConference.isEditing}
        close={data.newConference.cancel}
        isCreate
      />

      <CopConferenceEditDialog
        {...data.editConference}
        isOpen={data.editConference.isEditing}
        close={data.editConference.cancel}
      />

      <ReorderItemsDialog
        data={reorderData}
        title="Reorder COP conferences"
        itemKey={o => o._id.toString()}
        ItemComponent={CopConferenceReordering}
      />
    </>
  );
};